<script setup>
import { computed } from 'vue';
import { enterAnimation, leaveAnimation } from 'utils/slideAnimation';
import { useCompetitorAppConfig } from 'src/composables/useCompetitorAppConfig';
import TechnicalPropertyInfo from 'components/competitor-app-v2/technical-properties/TechnicalPropertyInfo.vue';
import TechnicalPropertySimpleValue from 'components/competitor-app-v2/technical-properties/TechnicalPropertySimpleValue.vue';
import ChromaHueCard from 'components/competitor-app-v2/technical-properties/ChromaHueCard.vue';
import { useToggleable } from 'src/composables/useToggleable';
import { sortArray } from 'utils';
import ChromaHueLightnessCard from 'components/competitor-app-v2/technical-properties/ChromaHueLightnessCard.vue';
import SiSaSgCard from 'components/competitor-app-v2/technical-properties/SiSaSgCard.vue';
import CieLabCard from 'components/competitor-app-v2/technical-properties/CieLabCard.vue';

const props = defineProps({
  technicalProperties: {
    type: Array,
    required: true,
  },
  mediumCode: {
    type: String,
    required: true
  },
  color: {
    type: String,
    required: false,
    default: '#7a7a7a',
  },
});

const byPropertyType = computed(() => {
  const result = {};

  sortArray(props.technicalProperties, 'propertyTypeConfig.title').forEach((row) => {
    if (!result[row.propertyType]) {
      result[row.propertyType] = [];
    }

    result[row.propertyType].push(row);
  });

  Object.keys(result).forEach((propertyType) => {
    result[propertyType] = sortArray(result[propertyType], 'applicationTypeConfig.sort');
  });

  return result;
});

const { getMedium, getTechnicalPropertyType } = useCompetitorAppConfig();

/* all properties with a "simple" value, whose visualization is clearly defined (using TechnicalPropertySimpleValue component).
* all other properties (like chroma/hue) have very specific visualization requirements and therefore have to be treated separately */
const simpleValuesByPropertyType = computed(() => (
  Object.fromEntries(
    Object.keys(byPropertyType.value)
      .filter((propertyType) => {
        const { dataType } = getTechnicalPropertyType(propertyType);

        return [
          'number',
          'text',
          'rgb',
          'nested_list',
        ].includes(dataType);
      })
      .map((propertyType) => [propertyType, byPropertyType.value[propertyType]])
  )
));

const chromaHueChartData = computed(() => {
  if (byPropertyType.value.chroma && byPropertyType.value.hue) {
    return {
      chroma: byPropertyType.value.chroma,
      hue: byPropertyType.value.hue,
    };
  }

  return null;
});

const chromaHueLightnessData = computed(() => {
  if (byPropertyType.value.chroma
    || byPropertyType.value.hue
    || byPropertyType.value.lightness) {
    return {
      chroma: byPropertyType.value.chroma,
      hue: byPropertyType.value.hue,
      lightness: byPropertyType.value.lightness,
    };
  }

  return null;
});

const siSaSgData = computed(() => {
  if (byPropertyType.value.si
    || byPropertyType.value.sa
    || byPropertyType.value.sg) {
    return {
      si: byPropertyType.value.si,
      sa: byPropertyType.value.sa,
      sg: byPropertyType.value.sg,
    };
  }

  return null;
});

const cieLabChartData = computed(() => {
  if (byPropertyType.value.a && byPropertyType.value.b) {
    return {
      a: byPropertyType.value.a,
      b: byPropertyType.value.b,
    };
  }

  return null;
});

const medium = computed(() => getMedium(props.mediumCode));

const { toggle, isOpen } = useToggleable();
</script>

<template>
  <div
    class="ce-technicalPropertiesByMedium"
    :class="{'ce-technicalPropertiesByMedium--open': isOpen}">
    <div
      class="header"
      @click="toggle">
      <h3>
        {{ medium?.title ?? mediumCode }}
      </h3>

      <fa-icon icon="angle-down" />
    </div>

    <transition
      :css="false"
      @enter="enterAnimation"
      @leave="leaveAnimation">
      <div v-if="isOpen">
        <div class="ce-technicalPropertiesWrapper">
          <div
            class="ce-technicalPropertyCard"
            v-for="(techProps, propertyType) in simpleValuesByPropertyType"
            :key="`${mediumCode}_${propertyType}`">
            <TechnicalPropertyInfo
              class="mb3"
              :property-type="propertyType" />

            <div class="ce-technicalPropertyValueWrapper ce-technicalPropertyValueWrapper--simple">
              <TechnicalPropertySimpleValue
                v-for="techProp in techProps"
                :key="`${techProp.medium}_${techProp.propertyType}_${techProp.applicationType}`"
                :technical-property="techProp" />
            </div>
          </div>

          <ChromaHueCard
            v-if="chromaHueChartData"
            :data="chromaHueChartData"
            :color="color" />

          <ChromaHueLightnessCard
            v-if="chromaHueLightnessData"
            :data="chromaHueLightnessData" />

          <SiSaSgCard
            v-if="siSaSgData"
            :data="siSaSgData" />

          <CieLabCard
            v-if="cieLabChartData"
            :data="cieLabChartData"
            :color="color" />
        </div>
      </div>
    </transition>
  </div>
</template>

<style lang="less">
@import (reference) '~styles/includes/base';

.ce-technicalPropertiesByMedium {
  .header {
    margin: @space-4 0 @space-2;
    padding-bottom: @space-1;
    border-bottom: 1px solid @color-text-base;
    .ms(1);
    display: flex;
    align-items: center;
    .noselect();
    cursor: pointer;

    h3 {
      margin: 0 @space-2 0 0;
      .ms(1);
    }

    svg {
      transition: transform 250ms ease-in-out;
    }
  }

  &--open {
    header {
      svg {
        transform: rotate(180deg);
      }
    }
  }

  .ce-technicalPropertiesWrapper {
    display: grid;
    grid-template-columns: 1fr;
    gap: @space-2;

    .ce-technicalPropertyCard {
      background: @color-mono-92;
      padding: @space-3;
      border-radius: @border-radius;

      .ce-technicalPropertyValueWrapper {
        &--simple {
          display: table;
        }
      }
    }

    @media screen and (min-width: 445px) {
      grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
    }
  }
}
</style>
